import { z } from "zod";
import { evidenceKindSchema } from "../events/index.js";
import { isoDateTimeSchema, severitySchema, uuidSchema } from "./common.js";
import { ledgerEventSchema } from "./registry.js";

export const findingStatusSchema = z.enum([
  "open",
  "triaged",
  "assigned",
  "in_progress",
  "awaiting_party",
  "resolved",
  "verified",
  "dismissed",
  "reopened",
  "auto_cleared",
]);
export type FindingStatus = z.infer<typeof findingStatusSchema>;

export const effortSchema = z.enum(["desk", "bilateral", "field_visit", "exchange"]);

/** The factors behind the impact score (docs/DATA_MODEL.md section 7, D-44). */
export const impactFactorsSchema = z.object({
  severityWeight: z.number(),
  exposureFactor: z.number(),
  volumeFactor: z.number(),
  ageFactor: z.number(),
  effort: effortSchema,
  effortWeight: z.number(),
});
export type ImpactFactors = z.infer<typeof impactFactorsSchema>;

export const findingSchema = z.object({
  id: uuidSchema,
  ref: z.string(),
  ruleId: z.string(),
  ruleName: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  spid: z.string().nullable(),
  severity: severitySchema,
  explanation: z.string(),
  facts: z.record(z.unknown()),
  status: findingStatusSchema,
  ownerPartyId: uuidSchema,
  ownerPartyName: z.string(),
  ownerTeamId: uuidSchema.nullable(),
  ownerTeamName: z.string().nullable(),
  assigneeUserId: uuidSchema.nullable(),
  assigneeName: z.string().nullable(),
  slaDueAt: isoDateTimeSchema,
  slaLate: z.boolean(),
  daysToSla: z.number().int(),
  impactScore: z.number(),
  impactFactors: impactFactorsSchema,
  firstRaisedAt: isoDateTimeSchema,
  createdAt: isoDateTimeSchema,
  isSynthetic: z.boolean(),
});
export type Finding = z.infer<typeof findingSchema>;

export const evidenceSchema = z.object({
  id: uuidSchema,
  kind: evidenceKindSchema,
  sha256: z.string(),
  fileName: z.string(),
  contentType: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  capturedAt: isoDateTimeSchema.nullable(),
  locationMismatch: z.boolean(),
  /** The file is in the store (always true for a field record or a cited read, which have no file). */
  received: z.boolean(),
  verified: z.boolean(),
  /** A signed link valid for minutes (D-55); null when the evidence has no file or the file never arrived. */
  downloadUrl: z.string().nullable(),
  attachedTo: z.array(z.object({ type: z.string(), id: z.string() })),
});
export type Evidence = z.infer<typeof evidenceSchema>;

export const findingDetailSchema = findingSchema.extend({
  route: z.array(z.string()),
  closeRequires: z.array(evidenceKindSchema),
  evidence: z.array(evidenceSchema),
  heldEvidence: z.array(evidenceSchema),
  dismissalReasons: z.array(z.object({ code: z.string(), label: z.string() })),
  history: z.array(ledgerEventSchema),
  allowedTransitions: z.array(findingStatusSchema),
});
export type FindingDetail = z.infer<typeof findingDetailSchema>;

export const transitionFindingSchema = z.object({
  to: findingStatusSchema,
  reason: z.string().optional(),
  reasonCode: z.string().optional(),
  assigneeUserId: uuidSchema.optional(),
  ownerPartyId: uuidSchema.optional(),
  ownerTeamId: uuidSchema.optional(),
  awaitingRef: z.string().optional(),
});
export type TransitionFinding = z.infer<typeof transitionFindingSchema>;

export const linkFindingEvidenceSchema = z
  .object({ evidenceId: uuidSchema.optional(), readId: uuidSchema.optional() })
  .refine((b) => (b.evidenceId ? 1 : 0) + (b.readId ? 1 : 0) === 1, {
    message: "name one of evidenceId or readId",
  });

export const bulkFindingActionSchema = z.enum(["assign", "raise_field_jobs", "export"]);
export const bulkUpdateFindingsSchema = z.object({
  findingIds: z.array(uuidSchema).min(1).max(1000),
  action: bulkFindingActionSchema,
  params: z.record(z.unknown()).optional(),
});

export const ruleSchema = z.object({
  id: z.string(),
  name: z.string(),
  entity: z.string(),
  enabled: z.boolean(),
  severity: severitySchema,
  params: z.record(z.unknown()),
  ownerParty: z.string(),
  ownerTeam: z.string(),
  route: z.array(z.string()),
  closeRequires: z.array(evidenceKindSchema),
  autoClear: z.boolean(),
  mpfRelevant: z.boolean(),
  overridden: z.boolean(),
  effort: effortSchema,
  threshold: z.string(),
  openCount: z.number().int().nonnegative(),
  changedBy: z.string().nullable(),
  changedAt: isoDateTimeSchema.nullable(),
  changeReason: z.string().nullable(),
});
export type Rule = z.infer<typeof ruleSchema>;

export const updateRuleConfigSchema = z.object({
  enabled: z.boolean().optional(),
  severity: severitySchema.optional(),
  params: z.record(z.unknown()).optional(),
  reason: z.string().min(5),
});

export const ruleRunSchema = z.object({
  id: uuidSchema,
  ref: z.string(),
  status: z.enum(["running", "completed", "failed"]),
  trigger: z.enum(["manual", "schedule", "field_job"]),
  /** The supply points a scoped run reconciled (D-62); null for a full run. */
  scope: z.object({ supplyPointIds: z.array(uuidSchema) }).nullable(),
  ruleIds: z.array(z.string()),
  startedAt: isoDateTimeSchema,
  finishedAt: isoDateTimeSchema.nullable(),
  dataAsOf: isoDateTimeSchema,
  durationMs: z.number().int().nullable(),
  timings: z.record(z.number().int()),
  error: z.string().nullable(),
  counts: z.object({
    raised: z.number().int(),
    updated: z.number().int(),
    autoCleared: z.number().int(),
    verified: z.number().int(),
    reopened: z.number().int(),
    clearedByEvidence: z.number().int(),
    byRule: z.record(z.number().int()),
    bySeverity: z.record(z.number().int()),
  }),
});
export type RuleRun = z.infer<typeof ruleRunSchema>;

export const startRuleRunSchema = z.object({ ruleIds: z.array(z.string()).optional() });
